import type {
    SupplierClusterMemberListParams,
    SupplierEntityListParams,
    SupplierRiskSignalFilters,
    SupplierRiskSignalListParams,
} from "../types/Supplier360Types";

function setExactOrIn(qs: URLSearchParams, field: string, value: string | string[] | undefined) {
    if (value === undefined) return;
    if (Array.isArray(value)) {
        if (value.length === 1) qs.set(field, value[0]);
        else if (value.length > 1) qs.set(`${field}__in`, value.join(','));
        return;
    }
    qs.set(field, value);
}

function setNumber(qs: URLSearchParams, field: string, value: number | undefined) {
    if (value !== undefined && Number.isFinite(value)) qs.set(field, String(value));
}

export function supplierEntityListUrl(databagId: number, params: SupplierEntityListParams): string {
    const qs = new URLSearchParams();
    qs.set('databag_id', String(databagId));
    if (params.entity_type) qs.set('entity_type', params.entity_type);
    setExactOrIn(qs, 'se_country__code_a2', params.se_country_code_a2);
    setNumber(qs, 'confidence__gte', params.confidence_gte);
    setNumber(qs, 'confidence__lte', params.confidence_lte);
    setNumber(qs, 'spend__gte', params.spend_gte);
    setNumber(qs, 'spend__lte', params.spend_lte);
    // A CSV filter on one param, not exact + `__in` like the others (§2.1).
    if (params.business_unit?.length) qs.set('business_unit', params.business_unit.join(','));
    if (params.search) qs.set('search', params.search);
    if (params.ordering) qs.set('ordering', params.ordering);
    qs.set('page', String(params.page));
    qs.set('page_size', String(params.pageSize));
    return `supplier_360/entity/?${qs.toString()}`;
}

export function supplierEntityDetailUrl(entityId: string | number): string {
    return `supplier_360/entity/${entityId}/`;
}

/**
 * "Remove one raw row from this cluster" (§2.1) — `POST {s_id, reason}`, answering with the
 * updated entity. The only removal path there is: `DELETE supplier_360/cluster_member/{id}/` is a
 * `405` (§2.2), because unclustering a raw row is an audited decision — it persists a
 * `NonMatchDecision` (§2.3) so re-clustering can't undo it — rather than a plain row delete.
 *
 * Addressed by the member's `s_id`, not its `ClusterMember.id`: `404` when that source row
 * isn't actually a member of this entity.
 */
export function supplierEntitySplitUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/split/`;
}

/**
 * The write path for an entity's `supplier_info`/`summary` text (§2.1, added 2026-09-18) — a
 * dedicated action, **not** a plain entity `PATCH`: both fields are read-only
 * `SerializerMethodField`s on the entity serializer, so a value sent in a plain `PATCH` body is
 * silently ignored. Answers `200` with the updated entity.
 *
 * A manual edit becomes its own `SupplierInfoEntry` (`is_manual: true`, ordered first) rather than
 * overwriting an automated source's entry — SV/D&B/the AI job keep their own entries in the array
 * alongside it, and a second edit by the same user updates that one entry in place.
 *
 * Docs §2.1 documents this as `PATCH` (its heading and its `curl` example both use it); §2.5/§3.3
 * refer to the same action as `POST` in passing. Following §2.1, the endpoint reference itself.
 */
export function supplierEntityEditInfoUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/edit_info/`;
}

/**
 * The entity's logo image bytes (§2.1) — not JSON: it streams the stored bytes with whatever
 * `Content-Type` Brandfetch returned, and `404`s when the entity has no logo (it re-checks
 * server-side, so a stale link 404s too). Only call it for an entity whose `logo_url` is non-empty.
 *
 * Built from the id rather than using `logo_url` verbatim: that field is a host-rooted path
 * (`/api/supplier_360/...`), which axios would append to the `/api` base URL instead of replacing it.
 */
export function supplierEntityLogoUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/logo/`;
}

/**
 * The profile header's KPI strip in one call (§2.1) — a pure per-entity aggregate, no
 * filters or pagination. Covers only the 4 pills that have a model behind them.
 */
export function supplierKpiSummaryUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/kpi_summary/`;
}

/**
 * The registry's KPI strip in one call (§2.25) — a `detail=False` action, so `databag_id` is
 * required (`400 {"databag_id": "required"}` without it). Databag-wide totals: nothing here
 * narrows with the tables' own search or column filters.
 */
export function supplierRegistrySummaryUrl(databagId: number): string {
    const qs = new URLSearchParams({databag_id: String(databagId)});
    return `supplier_360/entity/registry_summary/?${qs.toString()}`;
}

/**
 * Raw ERP rows behind the registry — the "Raw view" registry mode (§3.2).
 *
 * `search` matches `normalized_name` only (§2.2) — the frozen half of the "Supplier name" column's
 * `s_name (normalized_name)` display, so a match always corresponds to text the user can see in the row.
 */
export function supplierClusterMemberListUrl(databagId: number, params: SupplierClusterMemberListParams): string {
    const qs = new URLSearchParams();
    qs.set('databag_id', String(databagId));
    if (params.entity !== undefined) qs.set('entity', String(params.entity));
    if (params.s_id) qs.set('s_id', params.s_id);
    if (params.normalized_name_contains) qs.set('normalized_name__icontains', params.normalized_name_contains);
    setExactOrIn(qs, 'data_source', params.data_source?.map(String));
    setExactOrIn(qs, 'country__code_a2', params.country_code_a2);
    if (params.vat) qs.set('vat', params.vat);
    if (params.vat_contains) qs.set('vat__icontains', params.vat_contains);
    setExactOrIn(qs, 'match_basis', params.match_basis);
    setNumber(qs, 'spend__gte', params.spend_gte);
    setNumber(qs, 'spend__lte', params.spend_lte);
    if (params.search) qs.set('search', params.search);
    if (params.ordering) qs.set('ordering', params.ordering);
    qs.set('page', String(params.page));
    qs.set('page_size', String(params.pageSize));
    return `supplier_360/cluster_member/?${qs.toString()}`;
}

/**
 * Every source system (§2.24) — the Raw view's Source filter options. Not `supplier_360/`-prefixed
 * (`DataSource` lives in `data_processing`) and never paginated: a bare array, a handful of rows.
 */
export function dataSourceListUrl(): string {
    return 'data/source/';
}

/**
 * "Add raw row" (§2.2) — creates a manually-entered member on an existing entity's cluster.
 * The same collection URL the list reads from; `POST` is what makes it a create.
 */
export function supplierClusterMemberCreateUrl(): string {
    return 'supplier_360/cluster_member/';
}

/**
 * Cluster members for a single entity — Overview tab "Cluster" section (§3.3).
 *
 * Unpaginated (`no_page=1`, §1) — the section filters and sorts client-side, and derives the
 * source-system count and the alias list from the same rows, so a partial page would quietly
 * make all three wrong. One entity's cluster is small by nature.
 */
export function supplierClusterMembersByEntityUrl(entityId: number): string {
    return `supplier_360/cluster_member/?entity=${entityId}&no_page=1`;
}

/** Enrichment profile for a single entity — Overview tab "Identity" section (§2.5, §3.3). Take results[0] as current. */
export function supplierEnrichmentProfileUrl(entityId: number): string {
    return `supplier_360/enrichment_profile/?entity=${entityId}`;
}

/**
 * The only write path on an enrichment profile (§2.5) — `contact_info.domain` alone, since
 * `business_overview` moved off this model entirely (2026-09-18, now `SupplierEntity.supplier_info`).
 * Takes the `EnrichmentProfile` id (from `results[0]`), **not** the entity id, and answers `201`
 * with a newly created row: this model is versioned, so an edit never mutates the row in place.
 */
export function supplierEnrichmentProfileEditUrl(profileId: number): string {
    return `supplier_360/enrichment_profile/${profileId}/edit/`;
}

/**
 * The same edit keyed by `entity` in the body instead of a profile id (§2.5, added 2026-09-14).
 * This is the only route that can record an entity's *first* edit: with no profile row there is
 * no id for the route above to address. Both stay live side by side.
 */
export function supplierEnrichmentProfileEditForEntityUrl(): string {
    return 'supplier_360/enrichment_profile/edit/';
}

/** Corporate relationships from a single entity (§2.4, §3.3). */
export function supplierRelationshipUrl(entityId: number): string {
    return `supplier_360/relationship/?from_entity=${entityId}`;
}

export function supplierRelationshipListUrl(): string {
    return 'supplier_360/relationship/';
}

export function supplierParentNamesUrl(databagId: number, search: string, pageSize: number): string {
    const qs = new URLSearchParams();
    qs.set('databag_id', String(databagId));
    if (search) qs.set('search', search);
    qs.set('page', '1');
    qs.set('page_size', String(pageSize));
    return `supplier_360/relationship/parent_names/?${qs.toString()}`;
}

export function supplierRelationshipDetailUrl(relationshipId: number): string {
    return `supplier_360/relationship/${relationshipId}/`;
}

/**
 * Every contact of one entity — both Identity-section multi-selects in one call (§2.23, §3.3),
 * split by `contact_type` client-side. Unpaginated: the fields hold the full list anyway.
 */
export function supplierContactListUrl(entityId: number): string {
    return `supplier_360/contact/?entity=${entityId}&no_page=1`;
}

/**
 * One contact assignment — `DELETE` unchecks that person in that multi-select (the `User` and
 * their other entities survive), `PATCH` edits `role`, the only field an edit can change (§2.23).
 */
export function supplierContactDetailUrl(contactId: number): string {
    return `supplier_360/contact/${contactId}/`;
}

/**
 * Contact create — `POST` with `{entity, contact_type, user_id, role?}`. `user_id` must name an
 * EXISTING `User` (§2.23, re-modeled 2026-10-23 per the user) — there is no way to bring a new
 * person's PII into being through this endpoint any more.
 */
export function supplierContactCreateUrl(): string {
    return 'supplier_360/contact/';
}

/**
 * The candidate search behind the Identity section's two contact multi-selects — real Mithra
 * accounts only (§2.23, re-modeled 2026-10-23 per the user; was the cross-app `Profile` pool before).
 * **Not** `supplier_360/`-prefixed: `/users/` is the shared, cross-app endpoint.
 *
 * `search` matches `first_name`, `last_name`, `email` and `username`. Paginated (2026-10-24, per the
 * user — restored `ProfileViewSet`'s old `DataPagination`, now on `UserViewSet` too), so a large
 * org's typeahead doesn't fetch everyone matching in one call.
 */
export function userSearchUrl(search: string, pageSize: number): string {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    qs.set('page_size', String(pageSize));
    return `users/?${qs.toString()}`;
}

/** Every compliance requirement query key starts with this — used to invalidate after a write. */
export const COMPLIANCE_REQUIREMENT_URL_PREFIX = 'supplier_360/compliance_requirement/';

/** POST target for a new compliance requirement (§2.8). */
export function complianceRequirementCollectionUrl(): string {
    return COMPLIANCE_REQUIREMENT_URL_PREFIX;
}

/**
 * The Compliance tab's records grid (§2.8a) — a MUIX server-side grid, same contract as the
 * Transactions tab's: params from `BaseMuixApi.getMuiXQuery()` plus the required `entity=<id>`.
 * Shares the requirement prefix, so a create / edit invalidates it.
 */
export function complianceRequirementGridUrl(): string {
    return `${COMPLIANCE_REQUIREMENT_URL_PREFIX}muix/grid/`;
}

/**
 * The Compliance tab's KPI strip (§2.8 `kpi_summary`) — per-entity totals in one call. Shares the
 * requirement prefix, so a create / edit invalidates it along with the table.
 */
export function complianceKpiSummaryUrl(entityId: number): string {
    return `${COMPLIANCE_REQUIREMENT_URL_PREFIX}kpi_summary/?entity=${entityId}`;
}

/**
 * Every BU qualification query key starts with this. A BU's `status` is recomputed server-side on
 * every requirement write (§2.7), so requirement / evidence writes invalidate these too.
 */
export const BU_QUALIFICATION_URL_PREFIX = 'supplier_360/bu_qualification/';

/** PATCH target for one compliance requirement (§2.8). */
export function complianceRequirementDetailUrl(id: number): string {
    return `${COMPLIANCE_REQUIREMENT_URL_PREFIX}${id}/`;
}

/** One BU qualification (§2.7) — the record detail page's qualification rail. */
export function buQualificationDetailUrl(id: number): string {
    return `${BU_QUALIFICATION_URL_PREFIX}${id}/`;
}

/** Every evidence query key starts with this — used to invalidate after an upload. */
export const COMPLIANCE_ARTIFACT_URL_PREFIX = 'supplier_360/compliance_artifact/';

/** POST target for "Upload evidence" (§2.9). */
export function complianceArtifactCollectionUrl(): string {
    return COMPLIANCE_ARTIFACT_URL_PREFIX;
}

/** Mints `{"download_url": ...}` for one evidence file (§2.9) — `404` when it has no file. */
export function complianceArtifactDownloadUrl(id: number): string {
    return `${COMPLIANCE_ARTIFACT_URL_PREFIX}${id}/download-url/`;
}

/** Every evidence file of one requirement (§2.9), unpaginated — a record has a handful at most. */
export function complianceArtifactsUrl(requirementId: number): string {
    return `${COMPLIANCE_ARTIFACT_URL_PREFIX}?satisfies_requirement=${requirementId}&no_page=1`;
}

/**
 * BU qualifications for a single entity (§2.7). Fetched unpaginated: it enriches the requirement
 * rows (§3.7) and is also the Business units table's full row set.
 */
export function supplierBuQualificationsUrl(entityId: number): string {
    return `${BU_QUALIFICATION_URL_PREFIX}?entity=${entityId}&no_page=1`;
}

/** The Business units view's KPI strip (§2.7 `kpi_summary`) — shares the BU prefix, so writes refresh it. */
export function buQualificationKpiSummaryUrl(entityId: number): string {
    return `${BU_QUALIFICATION_URL_PREFIX}kpi_summary/?entity=${entityId}`;
}

function riskSignalFilterQuery(entityId: number, filters: SupplierRiskSignalFilters): URLSearchParams {
    const qs = new URLSearchParams();
    qs.set('entity', String(entityId));
    setExactOrIn(qs, 'impact_type', filters.impact_type);
    setExactOrIn(qs, 'category', filters.category);
    setExactOrIn(qs, 'language', filters.language);
    if (filters.search) qs.set('search', filters.search);
    // TODO: send `ordering=-published_at` (newest first) once the backend declares ordering
    //  fields on risk_signal — §2.10 lists none, so it would be ignored today.
    return qs;
}

/**
 * One page of a single entity's risk signals (§2.10) — the signal feed. `impact_type`, `category`
 * and `language` take exact or `__in`; `search` matches `title` only.
 */
export function supplierRiskSignalsUrl(entityId: number, params: SupplierRiskSignalListParams): string {
    const qs = riskSignalFilterQuery(entityId, params);
    qs.set('page', String(params.page));
    qs.set('page_size', String(params.pageSize));
    return `supplier_360/risk_signal/?${qs.toString()}`;
}

/**
 * Every matching risk signal on a single entity, unpaginated (`no_page=1`) — for the Insights
 * tab's client-side rollups by category, which need every row because no aggregate endpoint
 * exists (§3.8).
 */
export function supplierAllRiskSignalsUrl(entityId: number, filters: SupplierRiskSignalFilters): string {
    const qs = riskSignalFilterQuery(entityId, filters);
    qs.set('no_page', '1');
    return `supplier_360/risk_signal/?${qs.toString()}`;
}

/**
 * Health scores for a single entity — six-domain health block (§2.11). Pinned to our own
 * deterministic source: the endpoint's plain default is "latest per domain across all sources"
 * (added 2026-10-21, now that SupplierHealthScore also carries SV's own, non-comparable-scale
 * rows), and this block's own intent is specifically "our own six-domain score ... not the
 * third-party one" (SemanticHealthBlock.tsx) — `source__name` is `InfoSource.name`, the stable
 * identity the backend upserts by, not a numeric id to look up first.
 */
const MITHRA_HEALTH_SCORING_SOURCE_NAME = 'Mithra health scoring';

export function supplierHealthScoreUrl(entityId: number): string {
    const qs = new URLSearchParams({
        entity: String(entityId),
        source__name: MITHRA_HEALTH_SCORING_SOURCE_NAME,
    });
    return `supplier_360/health_score/?${qs.toString()}`;
}

/** Segmentation score for a single entity (§2.12). One-to-one — at most one row. */
export function supplierSegmentationUrl(entityId: number): string {
    return `supplier_360/segmentation/?entity=${entityId}`;
}

/**
 * The one segmentation write (§2.12) — direct field edits aren't allowed: every change has to go
 * through the audit trail. Keyed by `entity` in the **body**, not by a `SegmentationScore` id in
 * the path, so the same route also records an entity's very first score: a not-yet-scored entity
 * has no score row, and so no id, for a detail route to address. `reason` is mandatory only when
 * the entity already has a score.
 */
export function supplierSegmentationOverrideUrl(): string {
    return 'supplier_360/segmentation/override/';
}

/** Segmentation audit trail for a single entity (§2.13). Most recent first. */
export function supplierSegmentationAuditUrl(entityId: number): string {
    return `supplier_360/segmentation_audit/?entity=${entityId}`;
}

/**
 * One supplier's transaction ledger — the Transactions tab (§2.21, §3.4). A MUIX server-side grid
 * (`filterModel`/`sortModel`/`page`/`related_data` query params), not a plain list endpoint, so the
 * caller builds its params with `BaseMuixApi.getMuiXQuery()` and adds this endpoint's own required
 * `entity=<id>`: the backend resolves that to the whole cluster (every `ClusterMember`'s own
 * `s_id`), so the FE never has to assemble that list itself.
 */
export function supplierTransactionsGridUrl(): string {
    return 'supplier_360/transaction/muix/grid/';
}

/**
 * Spend breakdown for a single entity — Overview tab "Spend summary" section (§2.22, §3.3).
 * All three breakdowns (source system / category / business unit) come back in one call, so
 * the Segmented control switches locally with no extra round trip.
 */
export function supplierSpendSummaryUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/spend_summary/`;
}

/** Monthly spend, last 12 months vs the same months a year earlier — "Spend trend" (§2.22, §3.3). */
export function supplierSpendTrendUrl(entityId: number): string {
    return `supplier_360/entity/${entityId}/spend_trend/`;
}

/**
 * The databag's value-chain credit entitlement (§2.14) — `databag_id` is unique, so at most one
 * row. Read-only: when a databag has none, nothing in the API can create it, and unlock /
 * request-credits (both keyed by the entitlement id) can't be called either.
 */
export function chainEntitlementUrl(databagId: number): string {
    return `supplier_360/chain_entitlement/?databag_id=${databagId}`;
}

/**
 * Spend one credit to unlock `{entity}` for good (§2.14). `201` new unlock, `200` already
 * unlocked (free), `402 {"detail": "no credits remaining"}` with a zero balance.
 */
export function chainUnlockActionUrl(entitlementId: number): string {
    return `supplier_360/chain_entitlement/${entitlementId}/unlock/`;
}

/** Ask for more credits (§2.14) — `{requested_amount, approver?}`, answering `201` with a pending request. */
export function chainRequestCreditsUrl(entitlementId: number): string {
    return `supplier_360/chain_entitlement/${entitlementId}/request-credits/`;
}

/**
 * Every unlock under one entitlement (§2.15), unpaginated — one row per mapped supplier, so the
 * list is bounded by the credits granted.
 */
export function chainUnlockListUrl(entitlementId: number): string {
    return `supplier_360/chain_unlock/?entitlement=${entitlementId}&no_page=1`;
}

/** Pending credit requests under one entitlement (§2.16), newest first, unpaginated. */
export function chainPendingCreditRequestsUrl(entitlementId: number): string {
    return `supplier_360/chain_credit_request/?entitlement=${entitlementId}&status=pending&no_page=1`;
}

/** One credit request (§2.16) — `DELETE` is the only way to withdraw it; no "withdrawn" status exists. */
export function chainCreditRequestDetailUrl(requestId: number): string {
    return `supplier_360/chain_credit_request/${requestId}/`;
}

/**
 * One entity's value chain (§2.17) as a tree: `upstream` / `downstream` nested through `children`,
 * up to the API's default (and max) depth of 3 tiers. Not paginated.
 */
export function valueChainTreeUrl(entityId: number): string {
    return `supplier_360/value_chain_link/tree/?entity=${entityId}`;
}
